/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B050030 仕入明細照会
 */

import { Column, Query } from "@material-table/core";
import {
  B000170ShireNmr,
  B000200JanshohinNm1,
  B000200ShohinNmr,
  B050020Get,
  B050030Find,
  B050030FindRequest,
  B050030GetExcel,
  catchFileError,
  catchRestError,
} from "clients/rest";
import {
  msgState,
  SCodeSelect,
  SDateField,
  SFileButton,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  SText,
  STextField,
} from "components";
import { getClientError, getClientMessage } from "messages";
import { B050030SyuturyokuKbn, getSCode, SCode } from "messages/codes";
import { labels } from "messages/labels";
import {
  B000130,
  B000140,
  B000170,
  B000200,
  B000202,
  B050010InputData,
} from "pages";
import { B000120 } from "pages/b000/B000120";
import { createRef, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import { date, download, mapIds } from "services";
import { editKbn, eigyoKengen } from "services/codes";
import {
  authState,
  focusState,
  menusState,
  navigateTypeState,
  useFocus,
} from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// 検索条件を格納するデータタイプ
type SearchedFields = {
  searched: boolean;
} & Partial<{
  fields: Fields;
  page: number;
  pageSize: number;
  sort: string[];
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  shireYmdFrom: string;
  shireYmdTo: string;
  eigyoCdFrom: string;
  eigyoCdTo: string;
  bumonCdFrom: string;
  bumonCdTo: string;
  shainCdFrom: string;
  shainCdTo: string;
  hachudenpyoNoFrom: string;
  hachudenpyoNoTo: string;
  shireCd: string;
  shireNmr: string;
  shodaibun: string;
  shochubun: string;
  shoshobun: string;
  shohinCd: string;
  shohinNmr: string;
  syuturyokuKbn: SCode;
  janCd: string;
  janshohinNm: string;
}>;

// テーブル定義
type Data = Partial<{
  gyoNo: string;
  uchiwakeNmr: string;
  shireYmdF: string;
  hachudenpyoNoF: string;
  shohinCd: string;
  shohinNm: string;
  shiresu: string;
  shireKin: string;
  shireNm: string;
  shainNm: string;
  bumonNmr: string;
  shiredenpyoNo: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  shireYmdFrom: "",
  shireYmdTo: "",
  eigyoCdFrom: "",
  eigyoCdTo: "",
  bumonCdFrom: "",
  bumonCdTo: "",
  shainCdFrom: "",
  shainCdTo: "",
  hachudenpyoNoFrom: "",
  hachudenpyoNoTo: "",
  shireCd: "",
  shireNmr: "",
  shodaibun: "",
  shochubun: "",
  shoshobun: "",
  shohinCd: "",
  shohinNmr: "",
  syuturyokuKbn: getSCode(B050030SyuturyokuKbn, "0"),
  janCd: "",
  janshohinNm: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行　", width: "4rem", align: "right" },
  {
    field: "uchiwakeNmr",
    title: "区分",
    width: "5rem",
  },
  {
    field: "shireYmdF",
    title: "仕入日",
    width: "6rem",
  },
  { field: "hachudenpyoNoF", title: "発注No.", width: "8rem" },
  { field: "shohinCd", title: "商品コード", width: "8rem" },
  { field: "shohinNm", title: "商品名", width: "20rem" },
  {
    field: "shiresu",
    title: "仕入数　",
    width: "6rem",
    align: "right",
    render: (data) => data.shiresu?.toLocaleString(),
  },
  {
    field: "shireKin",
    title: "仕入金額　",
    width: "9rem",
    align: "right",
    render: (data) => data.shireKin?.toLocaleString(),
  },

  { field: "shireNm", title: "仕入先", width: "15rem" },
  { field: "shainNm", title: "仕入担当者", width: "8rem" },
  { field: "bumonNmr", title: "仕入担当部門", width: "6rem" },
  { field: "shiredenpyoNo", title: "仕入伝票番号", hidden: true },
];

const focusId = "B050030";

// B050030 仕入明細照会
export const B050030 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

// 検索条件を格納するステート
const B050030State = atom<SearchedFields>({
  key: "B050030State",
  default: {
    searched: false,
    fields: defaultFields,
  },
});

const Page = (props: Props) => {
  // 状態管理の初期設定
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // テーブルの値取得用Ref
  const tableRef = createRef();
  const pageSize = 15;
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするステート
  const isDefaultPage = useRef(false);
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // テーブル行で選択されたデータ
  const [rowData, setRowData] = useState<Partial<Data>>();
  // 検索条件保存
  const [searchedFeields, setSearchedFields] = useRecoilState(B050030State);

  // メッセージ表示
  const setMsg = useSetRecoilState(msgState);

  // 画面遷移の処理
  const navigate = useNavigate();
  const [menuNavigate, setMenuNavigate] = useRecoilState(navigateTypeState);

  // ボタンの活性・非活性
  const [disabledHachuDetail, setDisabledHachuDetail] = useState(true);
  const [disabledHachuExcel, setDisabledHachuExcel] = useState(true);

  // ログインユーザー情報
  const auth = useRecoilValue(authState);
  // 所属営業所コードでの営業所コード入力制御
  const [isEigyoCdHonsha, setIsEigyoCdHonsha] = useState(true);
  // menusステート取得
  const menus = useRecoilValue(menusState);
  // 営業所権限取得
  const eigyosyoKengen = menus["050"].pages["050030"].eigyosyoKengen;
  const pageB050010 = menus["050"].pages["050010"];
  const pageB050011 = menus["050"].pages["050011"];

  // 初期表示イベント
  useEffect(() => {
    if (menuNavigate === "menu") {
      // 自営業所の設定
      // イベント利用権限定義マスタ.営業所権限が"0"(所属営業所のみ指定可)の場合、
      if (eigyosyoKengen === eigyoKengen.only) {
        // 自営業所コード(変更不可)の設定
        methods.setValue("eigyoCdFrom", auth?.eigyoCd!);
        methods.setValue("eigyoCdTo", auth?.eigyoCd!);
        // 入力部非活性化
        setIsEigyoCdHonsha(false);
      }
      // 利用権限定義マスタ.営業所権限が"1"(全営業所指定可権限)の場合、
      else if (eigyosyoKengen === eigyoKengen.all) {
        // 空白(入力可能)
        setIsEigyoCdHonsha(true);
      }

      // 仕入日初期表示
      (async () => {
        await methods.setValue("shireYmdFrom", date());
        methods.setValue("shireYmdTo", date());
        focus(1);
      })();
      return;
    }

    if (searchedFeields.searched) {
      // 検索条件が保存されている
      const fields = searchedFeields.fields;
      if (typeof fields !== "undefined") {
        methods.setValue("shireYmdFrom", fields.shireYmdFrom);
        methods.setValue("shireYmdTo", fields.shireYmdTo);
        methods.setValue("eigyoCdFrom", fields.eigyoCdFrom);
        methods.setValue("eigyoCdTo", fields.eigyoCdTo);
        methods.setValue("bumonCdFrom", fields.bumonCdFrom);
        methods.setValue("bumonCdTo", fields.bumonCdTo);
        methods.setValue("shainCdFrom", fields.shainCdFrom);
        methods.setValue("shainCdTo", fields.shainCdTo);
        methods.setValue("hachudenpyoNoFrom", fields.hachudenpyoNoFrom);
        methods.setValue("hachudenpyoNoTo", fields.hachudenpyoNoTo);
        methods.setValue("shireCd", fields.shireCd);
        methods.setValue("shireNmr", fields.shireNmr);
        methods.setValue("shodaibun", fields.shodaibun);
        methods.setValue("shochubun", fields.shochubun);
        methods.setValue("shoshobun", fields.shoshobun);
        methods.setValue("shohinCd", fields.shohinCd);
        methods.setValue("shohinNmr", fields.shohinNmr);
        methods.setValue("syuturyokuKbn", fields.syuturyokuKbn);
        methods.setValue("janCd", fields.janCd);
        methods.setValue("janshohinNm", fields.janshohinNm);
        setSearched(true);
        return;
      }
    }
  }, []);

  // テーブル行選択時
  const handleSelectTableRow = (_: any, rowData: Data | undefined) => {
    // 行が選択されていないときはボタン非活性
    const disabled = typeof rowData === "undefined";
    setDisabledHachuDetail(disabled);

    // データを保存
    setRowData(rowData);
  };

  // テーブル行ダブルクリック時
  const handleDoubleClickTableRow = (data: Data) => {
    // 仕入データの検索を行う
    B050020Get({ shiredenpyoNo: data.shiredenpyoNo })
      .then((value) => {
        // 月次更新日・発注伝票番号・受注伝票番号 いずれかが設定されている場合
        // ⇒参照モード
        const inputData: B050010InputData = {
          editKbn: editKbn.reference,
          shire: value.data,
        };
        // 月次更新日・発注伝票番号・受注伝票番号 いずれも設定されていない場合
        // ⇒修正モード
        if (
          !value.data[0].getsujiYmd &&
          !value.data[0].hachudenpyoNo &&
          !value.data[0].juchudenpyoNo
        ) {
          inputData.editKbn = editKbn.update;
        }

        setSearchedFields({ searched: true, fields: methods.getValues() });
        setMenuNavigate("next");

        // 仕入入力(本社)画面へ遷移する。
        if (pageB050010) {
          navigate("/b050010", { state: inputData });
          // 仕入入力(物流)画面へ遷移する。
        } else if (pageB050011) {
          navigate("/b050011", { state: inputData });
          // 上記のどちらでもない人
        } else {
          setMsg(getClientError("B050030_001"));
        }
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 詳細ボタンクリック時
  const handleClickShowDetailButton = () => {
    // 行が選択されている場合のみ実行する。
    if (typeof rowData === "undefined") {
      return;
    }
    // 仕入データの検索を行う
    B050020Get({ shiredenpyoNo: rowData.shiredenpyoNo })
      .then((value) => {
        // 月次更新日・発注伝票番号・受注伝票番号 いずれかが設定されている場合
        // ⇒参照モード
        const inputData: B050010InputData = {
          editKbn: editKbn.reference,
          shire: value.data,
        };
        // 月次更新日・発注伝票番号・受注伝票番号 いずれも設定されていない場合
        // ⇒修正モード
        if (
          !value.data[0].getsujiYmd &&
          !value.data[0].hachudenpyoNo &&
          !value.data[0].juchudenpyoNo
        ) {
          inputData.editKbn = editKbn.update;
        }

        setSearchedFields({ searched: true, fields: methods.getValues() });
        setMenuNavigate("next");

        // 仕入入力(本社)画面へ遷移する。
        if (pageB050010) {
          navigate("/b050010", { state: inputData });
          // 仕入入力(物流)画面へ遷移する。
        } else if (pageB050011) {
          navigate("/b050011", { state: inputData });
          // 上記のどちらでもない人
        } else {
          setMsg(getClientError("B050030_001"));
        }
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // Excel出力ボタンクリックイベント
  const handleClickExcelButton = (data: Data) => {
    // 検索フォームのデータを取得する。
    const fields = methods.getValues();

    // 出力順に応じたソート処理
    let order;
    if (fields.syuturyokuKbn?.value === "0") {
      order = ["shireYmd,asc", "shiredenpyoNo,asc", "shiregyoNo,asc"];
    } else if (fields.syuturyokuKbn?.value === "1") {
      order = ["shohinCd,asc", "shiredenpyoNo,asc", "shiregyoNo,asc"];
    } else if (fields.syuturyokuKbn?.value === "2") {
      order = ["shireCd,asc", "shiredenpyoNo,asc", "shiregyoNo,asc"];
    }

    // Excel出力パラメータを作成する。
    // 検索パラメータを作成する。
    const param: B050030FindRequest = {
      shireYmdFrom: fields.shireYmdFrom,
      shireYmdTo: fields.shireYmdTo,
      eigyoCdFrom: fields.eigyoCdFrom,
      eigyoCdTo: fields.eigyoCdTo,
      bumonCdFrom: fields.bumonCdFrom,
      bumonCdTo: fields.bumonCdTo,
      shainCdFrom: fields.shainCdFrom,
      shainCdTo: fields.shainCdTo,
      hachudenpyoNoFrom: fields.hachudenpyoNoFrom,
      hachudenpyoNoTo: fields.hachudenpyoNoTo,
      shireCd: fields.shireCd,
      shodaibun: fields.shodaibun,
      shochubun: fields.shochubun,
      shoshobun: fields.shoshobun,
      shohinCd: fields.shohinCd,
      janCd: fields.janCd,
      // ソートの設定
      sort: order,
    };
    // API呼び出し
    B050030GetExcel(param)
      .then((response) => {
        download(response);
        setMsg(getClientMessage("B050030_001"));
      })
      .catch((reason) => {
        catchFileError(reason, navigate, setMsg);
      });
  };

  // 検索時の初期化処理
  const handleResetSearch = () => {
    // 各ボタン非活性化
    setDisabledHachuDetail(true);
    setDisabledHachuExcel(true);
    // 選択されているデータを空にする
    setRowData({});
    (tableRef.current as any).handleClearSelect();
  };

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 初期化
    handleResetSearch();
    // excelボタン非活性化
    setDisabledHachuExcel(true);
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }

      // 検索フォームのデータを取得する。
      const fields = methods.getValues();

      // 出力順に応じたソート処理
      let order;
      if (fields.syuturyokuKbn?.value === "0") {
        order = ["shireYmd,asc", "shiredenpyoNo,asc", "shiregyoNo,asc"];
      } else if (fields.syuturyokuKbn?.value === "1") {
        order = ["shohinCd,asc", "shiredenpyoNo,asc", "shiregyoNo,asc"];
      } else if (fields.syuturyokuKbn?.value === "2") {
        order = ["shireCd,asc", "shiredenpyoNo,asc", "shiregyoNo,asc"];
      }

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B050030FindRequest = {
        shireYmdFrom: fields.shireYmdFrom,
        shireYmdTo: fields.shireYmdTo,
        eigyoCdFrom: fields.eigyoCdFrom,
        eigyoCdTo: fields.eigyoCdTo,
        bumonCdFrom: fields.bumonCdFrom,
        bumonCdTo: fields.bumonCdTo,
        shainCdFrom: fields.shainCdFrom,
        shainCdTo: fields.shainCdTo,
        hachudenpyoNoFrom: fields.hachudenpyoNoFrom,
        hachudenpyoNoTo: fields.hachudenpyoNoTo,
        shireCd: fields.shireCd,
        shodaibun: fields.shodaibun,
        shochubun: fields.shochubun,
        shoshobun: fields.shoshobun,
        shohinCd: fields.shohinCd,
        janCd: fields.janCd,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        sort: order,
      };
      // API呼出し
      B050030Find(param)
        // 正常値取得
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
          // Excel出力ボタン活性化
          setDisabledHachuExcel(value.data.content.length <= 0);
        })
        // エラー時
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  //   レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="仕入明細照会"
        gridTemplateColumns="6rem 4rem 4rem 1rem 4rem 4rem 3rem 3rem 6rem 9rem 1rem 9rem 3rem"
      >
        <SLabel>仕入日</SLabel>
        <SDateField name="shireYmdFrom" gridColumn="span 2" {...register(1)} />
        <SText>～</SText>
        <SDateField name="shireYmdTo" gridColumn="span 2" {...register(2)} />

        <SLabel gridColumn="9">営業所</SLabel>
        {isEigyoCdHonsha ? (
          <STextField
            code
            label="営業所コード（開始）"
            name="eigyoCdFrom"
            {...register(5)}
            size={6}
            dialog={<B000120 />}
            handleSubmit={(data: any) => {
              methods.setValue("eigyoCdFrom", data.eigyoCd);
            }}
          />
        ) : (
          <STextField
            label="営業所コード（開始）"
            name="eigyoCdFrom"
            disabled
          />
        )}
        <SText>～</SText>
        {isEigyoCdHonsha ? (
          <STextField
            code
            label="営業所コード（終了）"
            name="eigyoCdTo"
            {...register(6)}
            size={6}
            dialog={<B000120 />}
            handleSubmit={(data: any) => {
              methods.setValue("eigyoCdTo", data.eigyoCd);
            }}
          />
        ) : (
          <STextField label="営業所コード（終了）" name="eigyoCdTo" disabled />
        )}

        <SLabel gridColumn="1">仕入担当部門</SLabel>
        <SNumberField
          name="bumonCdFrom"
          code
          size={6}
          gridColumn="span 2"
          dialog={<B000130 />}
          handleSubmit={(data: any) => {
            methods.setValue("bumonCdFrom", data.bumonCd);
          }}
          {...register(7)}
        />
        <SText>～</SText>
        <SNumberField
          name="bumonCdTo"
          code
          size={6}
          gridColumn="span 2"
          dialog={<B000130 />}
          handleSubmit={(data: any) => {
            methods.setValue("bumonCdTo", data.bumonCd);
          }}
          {...register(8)}
        />
        <SLabel gridColumn="9">仕入担当者</SLabel>
        <SNumberField
          name="shainCdFrom"
          code
          size={7}
          dialog={<B000140 />}
          handleSubmit={(data: any) => {
            methods.setValue("shainCdFrom", data.shainCd);
          }}
          {...register(9)}
        />
        <SText>～</SText>
        <SNumberField
          name="shainCdTo"
          code
          size={7}
          dialog={<B000140 />}
          handleSubmit={(data: any) => {
            methods.setValue("shainCdTo", data.shainCd);
          }}
          {...register(10)}
        />

        <SLabel gridColumn="1">発注No.</SLabel>
        <SNumberField
          name="hachudenpyoNoFrom"
          code
          size={10}
          {...register(11)}
          gridColumn="span 2"
        />
        <SText>～</SText>
        <SNumberField
          name="hachudenpyoNoTo"
          code
          size={10}
          gridColumn="span 2"
          {...register(12)}
        />

        <SLabel gridColumn="1">仕入先</SLabel>
        <SNumberField
          name="shireCd"
          code
          size={6}
          gridColumn="span 2"
          onBlur={() => B000170ShireNmr(methods, navigate, setMsg)}
          dialog={<B000170 />}
          handleSubmit={(data: any) => {
            methods.setValue("shireCd", data.shireCd);
            methods.setValue("shireNmr", data.shireNmr);
          }}
          {...register(13)}
        />
        <STextField name="shireNmr" size={24} disabled gridColumn="span 4" />

        <SLabel gridColumn="1">商品分類</SLabel>
        <SNumberField
          label="商品分類(大)"
          name="shodaibun"
          code
          size={3}
          {...register(15)}
        />
        <SNumberField
          label="商品分類(中)"
          name="shochubun"
          code
          size={3}
          {...register(16)}
        />
        <SNumberField
          label="商品分類(小)"
          name="shoshobun"
          code
          size={3}
          gridColumn="span 2"
          dialog={<B000202 />}
          handleSubmit={(data: any) => {
            if (data.shochubun === null) {
              methods.setValue("shodaibun", data.shodaibun);
              methods.setValue("shochubun", "");
              methods.setValue("shoshobun", "");
            } else if (data.shoshobun === null) {
              methods.setValue("shodaibun", data.shodaibun);
              methods.setValue("shochubun", data.shochubun);
              methods.setValue("shoshobun", "");
            } else {
              methods.setValue("shodaibun", data.shodaibun);
              methods.setValue("shochubun", data.shochubun);
              methods.setValue("shoshobun", data.shoshobun);
            }
          }}
          {...register(17)}
        />
        <SLabel gridColumn="9">商品</SLabel>
        <SNumberField
          label="商品コード"
          name="shohinCd"
          code
          size={10}
          onBlur={() => B000200ShohinNmr(methods, navigate, setMsg)}
          dialog={<B000200 />}
          handleSubmit={(data: any) => {
            methods.setValue("shohinCd", data.shohinCd);
            methods.setValue("shohinNmr", data.shohinNmr);
          }}
          {...register(18)}
        />
        <STextField name="shohinNmr" size={24} disabled gridColumn="span 3" />

        <SLabel gridColumn="1">出力順</SLabel>
        <SCodeSelect
          label="出力順"
          name="syuturyokuKbn"
          options={B050030SyuturyokuKbn}
          gridColumn="span 2"
          {...register(20)}
        />

        <SLabel gridColumn="9">JANコード</SLabel>
        <SNumberField
          label="JANコード"
          name="janCd"
          code
          size={13}
          onBlur={() => B000200JanshohinNm1(methods, navigate, setMsg)}
          dialog={<B000200 />}
          handleSubmit={(data: any) => {
            methods.setValue("janCd", data.janCd);
            methods.setValue("janshohinNm", data.shohinNmr);
          }}
          gridColumn="span 2"
          {...register(21)}
        />
        <STextField name="janshohinNm" size={24} disabled gridColumn="span 2" />

        <SNormalButton
          label={labels.buttons.search}
          gridRow="9"
          gridColumn="13"
          align="right"
          onClick={handleClickSearchButton}
          {...register(23)}
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyoNo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          width={1260}
          height={450}
          fixed={4}
          onRowClick={handleSelectTableRow}
          handleDoubleClick={handleDoubleClickTableRow}
          pageSize={pageSize}
          onPageChange={handleResetSearch}
        />
      </SFrame>
      <SFrame gridTemplateColumns="68.7rem 4rem 0.5rem 5rem">
        <SNormalButton
          label={labels.buttons.detail}
          disabled={disabledHachuDetail}
          onClick={handleClickShowDetailButton}
          align="right"
          gridColumn={2}
          {...register(24)}
        />
        <SFileButton
          label={labels.buttons.excel}
          onClick={handleClickExcelButton}
          disabled={disabledHachuExcel}
          gridColumn={4}
          {...register(25)}
        />
      </SFrame>
    </FormProvider>
  );
};
